import { useEffect, useRef } from "react";

// Floating protective droplet: aura rings + orbiting particles, reacts to pointer.
const HeroCanvas = ({ className = "" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pointer = { x: 0.5, y: 0.5 };
    const particles = Array.from({ length: 26 }, (_, i) => ({
      angle: (i / 26) * Math.PI * 2,
      radius: 0.28 + Math.random() * 0.22,
      speed: 0.0018 + Math.random() * 0.0028,
      size: 1.5 + Math.random() * 2.8,
      hue: Math.random() > 0.75 ? "255,107,107" : "125,211,252",
    }));

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = (e.clientX - rect.left) / Math.max(rect.width, 1);
      pointer.y = (e.clientY - rect.top) / Math.max(rect.height, 1);
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    let t = 0;
    const draw = () => {
      t += 1;
      ctx.clearRect(0, 0, w, h);
      const cx = w * 0.5 + (pointer.x - 0.5) * 26;
      const cy = h * 0.5 + (pointer.y - 0.5) * 20 + Math.sin(t * 0.012) * 9;
      const base = Math.min(w, h);

      // pulse rings
      for (let i = 0; i < 3; i++) {
        const phase = ((t * 0.006 + i / 3) % 1);
        const r = base * (0.16 + phase * 0.34);
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(2, 132, 199, ${0.22 * (1 - phase)})`;
        ctx.lineWidth = 1.6;
        ctx.stroke();
      }

      // orbiting particles
      particles.forEach((p) => {
        p.angle += p.speed;
        const px = cx + Math.cos(p.angle) * base * p.radius;
        const py = cy + Math.sin(p.angle) * base * p.radius * 0.72;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.hue}, 0.55)`;
        ctx.fill();
      });

      // droplet glow
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, base * 0.24);
      g.addColorStop(0, "rgba(2, 132, 199, 0.30)");
      g.addColorStop(0.6, "rgba(2, 132, 199, 0.10)");
      g.addColorStop(1, "rgba(2, 132, 199, 0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, base * 0.24, 0, Math.PI * 2);
      ctx.fill();

      // droplet body
      const s = base * 0.085;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.beginPath();
      ctx.moveTo(0, -s * 1.6);
      ctx.bezierCurveTo(s * 1.25, -s * 0.25, s * 0.95, s * 0.95, 0, s * 1.15);
      ctx.bezierCurveTo(-s * 0.95, s * 0.95, -s * 1.25, -s * 0.25, 0, -s * 1.6);
      const dg = ctx.createLinearGradient(0, -s * 1.6, 0, s * 1.15);
      dg.addColorStop(0, "#38BDF8");
      dg.addColorStop(1, "#0F2C59");
      ctx.fillStyle = dg;
      ctx.fill();
      // heart inside
      const hs = s * 0.42;
      ctx.beginPath();
      ctx.moveTo(0, hs * 1.1);
      ctx.bezierCurveTo(-hs * 2.1, -hs * 0.35, -hs * 0.95, -hs * 1.35, 0, -hs * 0.35);
      ctx.bezierCurveTo(hs * 0.95, -hs * 1.35, hs * 2.1, -hs * 0.35, 0, hs * 1.1);
      ctx.fillStyle = "#FF6B6B";
      ctx.fill();
      ctx.restore();

      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
    };
  }, []);

  return (
    <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden="true">
      <canvas ref={canvasRef} data-testid="hero-canvas" />
    </div>
  );
};

export default HeroCanvas;
